export function getLiveCaptionAudioTracks(inputs) {
    return inputs.flatMap(input => {
        if (input?.mediaType !== 'audio' || !input.jitsiTrack || typeof input.jitsiTrack !== 'object')
            return [];
        const track = input.jitsiTrack.track;
        return typeof MediaStreamTrack !== 'undefined' && track instanceof MediaStreamTrack
            && track.kind === 'audio' && track.readyState === 'live' ? [track] : [];
    });
}
/** Own only the mixer's nodes/listeners. Input tracks belong to the conference. */
export class CaptionAudioMixer {
    constructor(context, destination, failed, changed) {
        this.context = context;
        this.destination = destination;
        this.failed = failed;
        this.changed = changed;
        this.sources = new Map();
        this.disposed = false;
    }
    sync(tracks) {
        if (this.disposed)
            return;
        const current = new Set(tracks.filter(track => track.readyState === 'live' && track.kind === 'audio'));
        for (const [track, source] of this.sources) {
            if (!current.has(track))
                this.release(track, source);
        }
        for (const track of current) {
            if (this.sources.has(track))
                continue;
            const node = this.context.createMediaStreamSource(new MediaStream([track]));
            const source = { node, ended: () => {
                    if (this.disposed || this.sources.get(track) !== source)
                        return;
                    try {
                        this.release(track, source);
                        this.changed(this.sources.size);
                    }
                    catch {
                        this.failed();
                    }
                } };
            // Retain the node before calling vendor APIs so a failed setup is disposable.
            this.sources.set(track, source);
            track.addEventListener('ended', source.ended);
            node.connect(this.destination);
        }
        this.changed(this.sources.size);
    }
    dispose() {
        if (this.disposed)
            return;
        this.disposed = true;
        let failed = false;
        for (const [track, source] of this.sources) {
            try {
                this.release(track, source);
            }
            catch {
                failed = true;
            }
        }
        if (failed)
            throw new Error('Caption audio source cleanup failed');
    }
    release(track, source) {
        this.sources.delete(track);
        let failed = false;
        try {
            track.removeEventListener('ended', source.ended);
        }
        catch {
            failed = true;
        }
        try {
            source.node.disconnect();
        }
        catch {
            failed = true;
        }
        if (failed)
            throw new Error('Caption audio source cleanup failed');
    }
}
